import { trackUpmarketProductDetailsPageSkuExpandableClick } from "@faire/web-api/events/upmarket/click/productDetailsPageSkuExpandable";
import { IProductOption } from "@faire/web-api/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { ExpandableText } from "@faire/web/ui/ExpandableText/ExpandableText";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

import {
  isValidSKU,
  getOpenSizingSKUsFromProduct,
} from "@faire/retailer/lib/SKUsUtils";
import { ProductAddToCartStateHooks } from "@faire/retailer/views/product/ProductDetails/AddToCart/useProductAddToCartState";

import { ProductAddToCartState } from "../AddToCart/ProductAddToCartState";
import {
  multipleSkuLabel,
  expandButtonLabel,
  collapseButtonLabel,
} from "../SKUDisplayTexts";

export const SKUsText: React.FunctionComponent<SKUsText.IProps> = ({
  product,
  selectedOption,
  productAddToCartState,
  actionWhenVisible,
}) => {
  if (productAddToCartState.isOpenSizingVariant) {
    return (
      <MultipleSKUs
        product={product}
        selectedOption={selectedOption}
        actionWhenVisible={actionWhenVisible}
      />
    );
  }

  return (
    <SingleSKU
      productAddToCartState={productAddToCartState}
      actionWhenVisible={actionWhenVisible}
    />
  );
};

namespace SKUsText {
  export interface IProps {
    product: IProductPageResponse.IPdpProduct;
    productAddToCartState: ProductAddToCartState | ProductAddToCartStateHooks;
    selectedOption: IProductOption | undefined;
    actionWhenVisible: () => void;
  }
}

const MultipleSKUs: React.FunctionComponent<MultipleSKUs.IProps> = ({
  product,
  selectedOption,
  actionWhenVisible,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const skus = getOpenSizingSKUsFromProduct(product, selectedOption);
  const viewRef = useDoActionWhenVisible(actionWhenVisible);
  if (!skus) {
    return null;
  }

  return (
    <Row ref={viewRef}>
      <Typography>{multipleSkuLabel(strictLocalize)}</Typography>
      <Spacer width="1x" />
      <ExpandableText
        content={skus}
        maxLines={1}
        collapsible
        expandButtonLabel={expandButtonLabel(strictLocalize)}
        collapseButtonLabel={collapseButtonLabel(strictLocalize)}
        expandCallback={() => {
          trackUpmarketProductDetailsPageSkuExpandableClick("expand", skus);
        }}
        collapseCallback={() => {
          trackUpmarketProductDetailsPageSkuExpandableClick("collapse", skus);
        }}
      />
    </Row>
  );
};

namespace MultipleSKUs {
  export interface IProps {
    product: IProductPageResponse.IPdpProduct;
    selectedOption: IProductOption | undefined;
    actionWhenVisible: () => void;
  }
}

const SingleSKU: React.FunctionComponent<SingleSKU.IProps> = ({
  productAddToCartState,
  actionWhenVisible,
}) => {
  const sku = productAddToCartState.selectedOption?.brand_code;
  const showSKUs = sku && isValidSKU(sku);
  const viewRef = useDoActionWhenVisible(actionWhenVisible);
  if (!showSKUs) {
    return null;
  }

  return (
    <Typography ref={viewRef}>
      <StrictLocalMsg
        defaultMessage="SKU {productSku}"
        description={{
          text: "Label for SKU (stock keeping unit). This label will be displayed right beside a SKU.",
          img: "https://cdn.faire.com/fastly/0afe439d7a8b28d77448079abe030c7007e65ad07deedcb8a27118634d7e97ea.png",
        }}
        values={{
          productSku: sku,
        }}
      />
    </Typography>
  );
};

namespace SingleSKU {
  export interface IProps {
    productAddToCartState: ProductAddToCartState | ProductAddToCartStateHooks;
    actionWhenVisible: () => void;
  }
}
